import { useId, useMemo, useState } from 'react';
import { POST_TYPES, TOPICS } from '../categorize';
import { POST_TYPE_LABELS, TOPIC_LABELS } from '../constants';
import { S } from '../strings';
import type { ParsedItem, PostType, Topic } from '../types';
import { CommunityCard } from './CommunityCard';

export interface CommunityViewProps {
  // Community items after the shell's mute filter (muted ones only when
  // "Näytä kaikki" is on). Never contains official items.
  items: ParsedItem[];
  // Community bookmark snapshots, mute-filtered the same way.
  bookmarkItems: ParsedItem[];
  // Whether any community item or community bookmark exists at all, before
  // mute filtering: false shows the "not activated yet" empty state.
  hasAny: boolean;
  // Free-text search from the header.
  searchQuery: string;
  isBookmarked: (id: string) => boolean;
  onToggleBookmark: (item: ParsedItem) => void;
  isMuted: (id: string) => boolean;
  onToggleMute: (item: ParsedItem) => void;
}

function authorOf(item: ParsedItem): string {
  return item.author || item.sourceFeed;
}

// Yhteisö tab: posts from hand-picked independent bloggers, newest first,
// with author / topic / post-type / bookmarks-only filters plus the header
// search. Filter state is local: nothing else reads it.
export function CommunityView({
  items,
  bookmarkItems,
  hasAny,
  searchQuery,
  isBookmarked,
  onToggleBookmark,
  isMuted,
  onToggleMute,
}: CommunityViewProps) {
  const [author, setAuthor] = useState('');
  const [topic, setTopic] = useState<Topic | null>(null);
  const [postType, setPostType] = useState<PostType | null>(null);
  const [bookmarksOnly, setBookmarksOnly] = useState(false);
  const authorSelectId = useId();
  const titleId = useId();

  const authors = useMemo(
    () =>
      Array.from(new Set([...items, ...bookmarkItems].map(authorOf).filter((a) => a))).sort(
        (a, b) => a.localeCompare(b, 'fi-FI'),
      ),
    [items, bookmarkItems],
  );

  // Bookmarks-only, author and search: the base the topic pills count from.
  const base = useMemo(() => {
    let list = bookmarksOnly ? bookmarkItems : items;
    if (author) list = list.filter((i) => authorOf(i) === author);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.summaryFI.toLowerCase().includes(q) ||
          authorOf(i).toLowerCase().includes(q),
      );
    }
    return list;
  }, [items, bookmarkItems, bookmarksOnly, author, searchQuery]);
  const byTopic = useMemo(
    () => (topic ? base.filter((i) => (i.topic ?? 'other') === topic) : base),
    [base, topic],
  );
  const filtered = useMemo(
    () => (postType ? byTopic.filter((i) => (i.postType ?? 'news') === postType) : byTopic),
    [byTopic, postType],
  );

  if (!hasAny) {
    return (
      <div
        role="status"
        style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}
      >
        <div aria-hidden="true" style={{ fontSize: 36, marginBottom: 12 }}>
          ✍️
        </div>
        <p style={{ margin: 0, fontSize: 14 }}>{S.community.empty}</p>
      </div>
    );
  }

  const topicCount = (t: Topic) => base.filter((i) => (i.topic ?? 'other') === t).length;
  const typeCount = (p: PostType) => byTopic.filter((i) => (i.postType ?? 'news') === p).length;

  return (
    <section aria-labelledby={titleId}>
      <h2
        id={titleId}
        style={{ fontSize: 16, fontWeight: 800, margin: '0 0 4px', color: 'var(--text-primary)' }}
      >
        {S.community.title}
      </h2>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }}>
        {S.community.intro}
      </p>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 12,
          paddingBottom: 12,
          borderBottom: '1px dashed var(--border)',
        }}
      >
        <button
          type="button"
          onClick={() => setBookmarksOnly((v) => !v)}
          aria-pressed={bookmarksOnly}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            borderRadius: 22,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            border: '2px solid #B8881F',
            background: bookmarksOnly ? '#B8881F' : 'transparent',
            color: bookmarksOnly ? '#fff' : '#B8881F',
            transition: 'all 0.15s',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>
            {bookmarksOnly ? '★' : '☆'}
          </span>
          <span>{S.community.bookmarksOnly}</span>
          <span style={{ opacity: 0.75 }}>{bookmarkItems.length}</span>
        </button>

        <label
          htmlFor={authorSelectId}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}
        >
          {S.community.author}
          <select
            id={authorSelectId}
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            style={{
              padding: '6px 8px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--card-bg)',
              color: 'var(--text-primary)',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          >
            <option value="">{S.community.allAuthors}</option>
            {authors.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
      </div>

      <PillGroup
        label={S.community.topic}
        options={TOPICS.map((t) => ({ value: t, label: TOPIC_LABELS[t], count: topicCount(t) }))}
        selected={topic}
        onSelect={setTopic}
      />
      <PillGroup
        label={S.community.postType}
        options={POST_TYPES.map((p) => ({
          value: p,
          label: POST_TYPE_LABELS[p],
          count: typeCount(p),
        }))}
        selected={postType}
        onSelect={setPostType}
      />

      <div
        role="status"
        style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 10px' }}
      >
        {S.community.count(filtered.length)}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
          {bookmarksOnly && bookmarkItems.length === 0
            ? S.community.emptyBookmarks
            : S.community.emptyFiltered}
        </div>
      ) : (
        <div>
          {filtered.map((item) => (
            <CommunityCard
              key={item.id}
              item={item}
              bookmarked={isBookmarked(item.id)}
              onToggleBookmark={onToggleBookmark}
              muted={isMuted(item.id)}
              onToggleMute={onToggleMute}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// "Kaikki" plus one toggle pill per value; values with no matching posts are
// hidden unless selected. Clicking the active pill clears the selection.
function PillGroup<T extends string>({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: { value: T; label: string; count: number }[];
  selected: T | null;
  onSelect: (value: T | null) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}
    >
      <span
        aria-hidden="true"
        style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginRight: 4 }}
      >
        {label}
      </span>
      <Pill active={selected === null} onClick={() => onSelect(null)}>
        {S.community.all}
      </Pill>
      {options
        .filter((o) => o.count > 0 || o.value === selected)
        .map((o) => (
          <Pill
            key={o.value}
            active={selected === o.value}
            onClick={() => onSelect(selected === o.value ? null : o.value)}
          >
            {o.label} {o.count}
          </Pill>
        ))}
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="pill"
      aria-pressed={active}
      onClick={onClick}
      style={{
        padding: '5px 12px',
        borderRadius: 16,
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
        border: '1.5px solid',
        transition: 'all 0.15s',
        borderColor: active ? '#6264A7' : 'var(--border)',
        background: active ? '#E8E8F7' : 'var(--card-bg)',
        color: active ? '#3B3A7A' : 'var(--text-muted)',
      }}
    >
      {children}
    </button>
  );
}
